/**
 * Account Quick Actions Component
 *
 * Dropdown menu with account lifecycle actions.
 * Actions are conditionally shown based on current account status.
 */
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  Modal,
  Input,
  Label,
} from '@respectly/ui';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  PauseCircle,
  PlayCircle,
  XCircle,
  UserCog,
  ScrollText,
  Trash2,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { AccountListItem } from '@respectly/shared-types';
import { useAccountsStore } from '../../stores/useAccountsStore';

// ============================================
// Types
// ============================================

interface AccountQuickActionsProps {
  account: AccountListItem;
  onImpersonate?: () => void;
}

interface ActionModalState {
  type: 'suspend' | 'cancel' | 'delete' | null;
  reason: string;
}

// ============================================
// Component
// ============================================

export function AccountQuickActions({ account, onImpersonate }: AccountQuickActionsProps) {
  const navigate = useNavigate();
  const { suspendAccount, reactivateAccount, cancelAccount, deleteAccount, isPerformingAction } =
    useAccountsStore();

  const [modalState, setModalState] = useState<ActionModalState>({
    type: null,
    reason: '',
  });

  // Determine available actions based on status
  const canSuspend = account.status === 'active' || account.status === 'trial';
  const canReactivate = account.status === 'suspended';
  const canCancel = account.status !== 'cancelled';
  const canDelete = account.status === 'cancelled';

  // ============================================
  // Handlers
  // ============================================

  const handleViewDetails = () => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  const handleEdit = () => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  const handleViewAudit = () => {
    navigate({ to: '/accounts/$id/$tab', params: { id: account.id, tab: 'audit' } });
  };

  const handleSuspend = async () => {
    if (!modalState.reason.trim()) return;

    const success = await suspendAccount(account.id, {
      reason: modalState.reason,
      notifyUsers: true,
    });

    if (success) {
      closeModal();
    }
  };

  const handleReactivate = async () => {
    const success = await reactivateAccount(account.id, {
      notifyUsers: true,
    });

    if (success) {
      closeModal();
    }
  };

  const handleCancel = async () => {
    if (!modalState.reason.trim()) return;

    const success = await cancelAccount(account.id, {
      reason: modalState.reason,
    });

    if (success) {
      closeModal();
    }
  };

  const handleDelete = async () => {
    const success = await deleteAccount(account.id);

    if (success) {
      closeModal();
    }
  };

  const openModal = (type: 'suspend' | 'cancel' | 'delete') => {
    setModalState({ type, reason: '' });
  };

  const closeModal = () => {
    setModalState({ type: null, reason: '' });
  };

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* View/Edit Actions */}
          <DropdownMenuItem onClick={handleViewDetails}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Account
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Lifecycle Actions */}
          {canSuspend && (
            <DropdownMenuItem
              onClick={() => openModal('suspend')}
              className="text-amber-600 focus:text-amber-600"
            >
              <PauseCircle className="mr-2 h-4 w-4" />
              Suspend Account
            </DropdownMenuItem>
          )}
          {canReactivate && (
            <DropdownMenuItem
              onClick={handleReactivate}
              className="text-emerald-600 focus:text-emerald-600"
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              Reactivate Account
            </DropdownMenuItem>
          )}
          {canCancel && (
            <DropdownMenuItem
              onClick={() => openModal('cancel')}
              className="text-red-600 focus:text-red-600"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Account
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Impersonation */}
          {onImpersonate && (
            <DropdownMenuItem onClick={onImpersonate}>
              <UserCog className="mr-2 h-4 w-4" />
              Impersonate User
            </DropdownMenuItem>
          )}

          {/* Audit */}
          <DropdownMenuItem onClick={handleViewAudit}>
            <ScrollText className="mr-2 h-4 w-4" />
            View Audit Log
          </DropdownMenuItem>

          {/* Delete (only for cancelled accounts) */}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => openModal('delete')}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Account
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Suspend Modal */}
      <Modal isOpen={modalState.type === 'suspend'} onClose={closeModal} title="Suspend Account">
        <p className="text-sm text-slate-500 mb-4">
          Are you sure you want to suspend "{account.name}"? Users will lose access until reactivated.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suspend-reason">Reason for suspension *</Label>
            <Input
              id="suspend-reason"
              placeholder="Enter reason for suspension..."
              value={modalState.reason}
              onChange={(e) => setModalState((s) => ({ ...s, reason: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={!modalState.reason.trim() || isPerformingAction}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isPerformingAction ? 'Suspending...' : 'Suspend Account'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal isOpen={modalState.type === 'cancel'} onClose={closeModal} title="Cancel Account">
        <p className="text-sm text-slate-500 mb-4">
          Are you sure you want to cancel "{account.name}"? This action cannot be easily undone.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason for cancellation *</Label>
            <Input
              id="cancel-reason"
              placeholder="Enter reason for cancellation..."
              value={modalState.reason}
              onChange={(e) => setModalState((s) => ({ ...s, reason: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={!modalState.reason.trim() || isPerformingAction}
            >
              {isPerformingAction ? 'Cancelling...' : 'Cancel Account'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={modalState.type === 'delete'} onClose={closeModal} title="Delete Account">
        <p className="text-sm text-slate-500 mb-4">
          Are you sure you want to permanently delete "{account.name}"? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isPerformingAction}>
            {isPerformingAction ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </div>
      </Modal>
    </>
  );
}
